import React, { useMemo } from 'react';
import styled from 'styled-components';
import { SiteDeliveryPlanningFragmentFragment } from 'data/__generated__';
import { DetailLink } from 'components/DetailsView/DetailLink';
import { isDateClosed, isDateLaunchOrRemoval } from '../utils/dateUtils';
import { FaChevronUp, FaChevronDown, FaSort } from 'react-icons/fa';
import { getWeekNumber, getTotemDayOfTheWeek } from 'helpers/dateTimes';

interface WeekDelimiter {
    position: number;
    weekType: 'previous' | 'current' | 'next';
    mondayDate: Date;
}

interface DeliveryPlanningTableProps {
    sitePlannings: SiteDeliveryPlanningFragmentFragment[];
    visibleDates: string[]; // Dates réelles ["2025-06-09", "2025-06-10", ...]
    visibleLabels: Array<{ weekday: string; shortDate: string }>; // Labels avec dates DD/MM
    weekDelimiters: WeekDelimiter[]; // Délimiteurs de semaines
    deliveryPlanningState: Record<string, Record<string, boolean>>; // siteId -> date -> isActive
    totemClosedDays: Array<{ date: string }>; // Jours fermés globaux
    sortConfig?: { date: string; direction: 'asc' | 'desc' } | null;
    onDeliveryPlanningUpdate: (siteId: string, date: string) => void;
    onSelectAllForDay: (date: string, isChecked: boolean) => void;
    onSort: (date: string) => void;
    selectedSiteIds?: Set<string>;
    onSiteSelect?: (siteId: string) => void;
    onSelectAllSites?: (isChecked: boolean) => void;
}

export const DeliveryPlanningTable: React.FC<DeliveryPlanningTableProps> = ({
    sitePlannings,
    visibleDates,
    visibleLabels,
    weekDelimiters,
    deliveryPlanningState,
    totemClosedDays,
    sortConfig,
    onDeliveryPlanningUpdate,
    onSelectAllForDay,
    onSort,
    selectedSiteIds,
    onSiteSelect,
    onSelectAllSites,
}) => {
    const sortedSitePlannings = useMemo(() => {
        if (!sortConfig) return sitePlannings;

        return [...sitePlannings].sort((a, b) => {
            const aChecked = deliveryPlanningState[a.site._id]?.[sortConfig.date] || false;
            const bChecked = deliveryPlanningState[b.site._id]?.[sortConfig.date] || false;

            if (aChecked === bChecked) return 0;

            return sortConfig.direction === 'asc' ? (aChecked ? -1 : 1) : bChecked ? -1 : 1;
        });
    }, [sitePlannings, deliveryPlanningState, sortConfig]);

    const getSelectAllState = (date: string) => {
        const availableSites = sortedSitePlannings.filter(
            ({ site }) =>
                !isDateClosed(date, site.closingDatesRanges, totemClosedDays, site.launchDate, site.removalDate),
        );

        if (availableSites.length === 0) return { checked: false, indeterminate: false };

        const checkedSites = availableSites.filter(
            (siteData) => deliveryPlanningState[siteData.site._id]?.[date] || false,
        );

        if (checkedSites.length === 0) {
            return { checked: false, indeterminate: false };
        } else if (checkedSites.length === availableSites.length) {
            return { checked: true, indeterminate: false };
        } else {
            return { checked: false, indeterminate: true };
        }
    };

    const getWeekDelimiterInfo = (index: number) => {
        const delimiter = weekDelimiters.find((d) => d.position === index);
        if (!delimiter) return null;

        return {
            weekType: delimiter.weekType,
            weekNumber: getWeekNumber(delimiter.mondayDate),
        };
    };

    // Calculate how many days each week delimiter should span
    const getWeekSpan = (weekDelimiterIndex: number) => {
        const currentDelimiter = weekDelimiters[weekDelimiterIndex];
        if (!currentDelimiter) return 1;

        const nextDelimiter = weekDelimiters[weekDelimiterIndex + 1];
        const startPosition = currentDelimiter.position;
        const endPosition = nextDelimiter ? nextDelimiter.position : visibleDates.length;

        return endPosition - startPosition;
    };

    const getWeekDelimiterInfoWithSpan = (index: number) => {
        const delimiter = weekDelimiters.find((d) => d.position === index);

        if (!delimiter) return null;

        const delimiterIndex = weekDelimiters.findIndex((d) => d.position === index);
        const span = getWeekSpan(delimiterIndex);

        return {
            weekType: delimiter.weekType,
            weekNumber: getWeekNumber(delimiter.mondayDate),
            span: span,
        };
    };

    const isFirstWeekDelimiter = (index: number) => {
        const delimiterInfo = getWeekDelimiterInfo(index);
        return delimiterInfo && weekDelimiters.findIndex((d) => d.position === index) === 0;
    };

    const getDeliveryPlanningCount = (date: string) => {
        const availableSites = sortedSitePlannings.filter(
            ({ site }) =>
                !isDateClosed(date, site.closingDatesRanges, totemClosedDays, site.launchDate, site.removalDate),
        );

        const checkedSites = availableSites.filter(
            (siteData) => deliveryPlanningState[siteData.site._id]?.[date] || false,
        );

        return `${checkedSites.length}/${availableSites.length}`;
    };

    const isSiteSelectionEnabled = !!selectedSiteIds && !!onSiteSelect && !!onSelectAllSites;
    const isSiteSelected = (siteId: string): boolean => selectedSiteIds?.has(siteId) ?? false;
    const areAllVisibleSitesSelected =
        isSiteSelectionEnabled &&
        sortedSitePlannings.length > 0 &&
        sortedSitePlannings.every(({ site }) => isSiteSelected(site._id));
    const hasPartiallySelectedVisibleSites =
        isSiteSelectionEnabled &&
        sortedSitePlannings.some(({ site }) => isSiteSelected(site._id)) &&
        !areAllVisibleSitesSelected;

    return (
        <DeliveryPlanningTableContainer>
            <TableContainer>
                <SitesTableContainer $visibleDatesCount={visibleDates.length}>
                    <WeekHeaderRow>
                        <WeekSiteHeaderCell />
                        {visibleLabels.map((label, index) => {
                            const delimiterInfo = getWeekDelimiterInfoWithSpan(index);

                            // Skip rendering cells that are covered by a previous week delimiter
                            let skipThisCell = false;
                            for (let i = 0; i < index; i++) {
                                const prevDelimiterInfo = getWeekDelimiterInfoWithSpan(i);
                                if (prevDelimiterInfo && i + prevDelimiterInfo.span > index) {
                                    skipThisCell = true;
                                    break;
                                }
                            }

                            if (skipThisCell) {
                                return null;
                            }

                            const isFirstWeekDelimiter =
                                delimiterInfo && weekDelimiters.findIndex((d) => d.position === index) === 0;

                            return (
                                <WeekHeaderCell
                                    key={index}
                                    $isWeekStart={!!delimiterInfo}
                                    $hasWeekDelimiter={!!delimiterInfo}
                                    $weekSpan={delimiterInfo?.span || 1}
                                    $isFirstWeekDelimiter={!!isFirstWeekDelimiter}
                                >
                                    {delimiterInfo && <WeekDelimiter>Semaine {delimiterInfo.weekNumber}</WeekDelimiter>}
                                </WeekHeaderCell>
                            );
                        })}
                    </WeekHeaderRow>
                    <HeaderRow>
                        <SiteHeaderCell>
                            {isSiteSelectionEnabled && (
                                <SiteSelectionCheckbox
                                    type="checkbox"
                                    checked={areAllVisibleSitesSelected}
                                    ref={(input) => {
                                        if (input) {
                                            input.indeterminate = hasPartiallySelectedVisibleSites;
                                        }
                                    }}
                                    onChange={(event) => onSelectAllSites(event.target.checked)}
                                    title="Sélectionner tous les sites visibles"
                                />
                            )}
                            <span>Sites</span>
                        </SiteHeaderCell>
                        {visibleLabels.map((label, index) => {
                            const date = visibleDates[index];
                            const selectAllState = getSelectAllState(date);
                            const delimiterInfo = getWeekDelimiterInfo(index);
                            const isFirstDelimiter = isFirstWeekDelimiter(index);
                            const deliveryCount = getDeliveryPlanningCount(date);

                            return (
                                <DateHeaderCell
                                    key={index}
                                    $isWeekStart={!!delimiterInfo}
                                    $isFirstWeekStart={!!isFirstDelimiter}
                                >
                                    <DeliveryCountLabel>{deliveryCount}</DeliveryCountLabel>
                                    <DateHeaderContent>
                                        <WeekdayLabel>{label.weekday}</WeekdayLabel>
                                        <DateLabel>{label.shortDate}</DateLabel>
                                        <HeaderControls>
                                            <HeaderCheckbox
                                                type="checkbox"
                                                checked={selectAllState.checked}
                                                ref={(input) => {
                                                    if (input) {
                                                        input.indeterminate = selectAllState.indeterminate;
                                                    }
                                                }}
                                                onChange={(e) => onSelectAllForDay(date, e.target.checked)}
                                                title={
                                                    selectAllState.indeterminate
                                                        ? 'Partiellement sélectionné'
                                                        : selectAllState.checked
                                                          ? 'Désélectionner tous'
                                                          : 'Sélectionner tous'
                                                }
                                            />
                                            <SortButton
                                                type="button"
                                                onClick={() => onSort(date)}
                                                $isActive={sortConfig?.date === date}
                                                title="Trier par statut"
                                            >
                                                {sortConfig?.date === date ? (
                                                    sortConfig.direction === 'asc' ? (
                                                        <FaChevronUp />
                                                    ) : (
                                                        <FaChevronDown />
                                                    )
                                                ) : (
                                                    <FaSort />
                                                )}
                                            </SortButton>
                                        </HeaderControls>
                                    </DateHeaderContent>
                                </DateHeaderCell>
                            );
                        })}
                    </HeaderRow>
                    {sortedSitePlannings.map(({ site, deliveryPlanning }) => (
                        <SiteRow key={site._id}>
                            <SiteInfoCell>
                                {isSiteSelectionEnabled && (
                                    <SiteSelectionCheckbox
                                        type="checkbox"
                                        checked={isSiteSelected(site._id)}
                                        onChange={() => onSiteSelect(site._id)}
                                        title={`Sélectionner ${site.name}`}
                                    />
                                )}
                                <DetailLink
                                    path="site"
                                    name={site.name}
                                    value={site._id}
                                    color="black"
                                    fontWeight="bold"
                                    fontSize="15px"
                                />
                            </SiteInfoCell>
                            {visibleDates.map((date, index) => {
                                const deliveryDate = deliveryPlanning.find((d) => d.date === date);
                                const isClosedDate = isDateClosed(
                                    date,
                                    site.closingDatesRanges,
                                    totemClosedDays,
                                    site.launchDate,
                                    site.removalDate,
                                );
                                const isLaunchOrRemovalDate = isDateLaunchOrRemoval(
                                    date,
                                    site.launchDate,
                                    site.removalDate,
                                );
                                const delimiterInfo = getWeekDelimiterInfo(index);
                                const isFirstDelimiter = isFirstWeekDelimiter(index);

                                const dateObj = new Date(date);
                                const dayIndex = getTotemDayOfTheWeek(dateObj);
                                const isDeliveryDay = site.deliveryDays[dayIndex] || false;

                                const isInteractionDisabled =
                                    isClosedDate ||
                                    (isLaunchOrRemovalDate && (deliveryPlanningState[site._id]?.[date] ?? false));

                                return (
                                    <DateCheckboxCell
                                        key={date}
                                        data-test="delivery-planning-cell"
                                        data-cell-id={`${site._id}|${date}`}
                                        data-disabled={isInteractionDisabled}
                                        $isActive={deliveryDate?.isActive ?? false}
                                        $exists={!!deliveryDate}
                                        $isClosed={isClosedDate}
                                        $isLaunchOrRemoval={isLaunchOrRemovalDate}
                                        $isWeekStart={!!delimiterInfo}
                                        $isFirstWeekStart={!!isFirstDelimiter}
                                    >
                                        <CheckboxInput
                                            type="checkbox"
                                            checked={deliveryPlanningState[site._id]?.[date] || false}
                                            onChange={() => {
                                                if (isLaunchOrRemovalDate && deliveryPlanningState[site._id]?.[date]) {
                                                    return;
                                                }
                                                onDeliveryPlanningUpdate(site._id, date);
                                            }}
                                            disabled={isClosedDate}
                                            $isActive={deliveryDate?.isActive ?? false}
                                            $exists={!!deliveryDate}
                                            $isClosed={isClosedDate}
                                            $isLaunchOrRemoval={isLaunchOrRemovalDate}
                                            $isDeliveryDay={isDeliveryDay}
                                        />
                                    </DateCheckboxCell>
                                );
                            })}
                        </SiteRow>
                    ))}
                </SitesTableContainer>
            </TableContainer>
        </DeliveryPlanningTableContainer>
    );
};

const DeliveryPlanningTableContainer = styled.div`
    flex: 1;
    display: flex;
    flex-direction: column;
    min-width: 0;
    width: 100%;
    overflow: auto;
`;

const TableContainer = styled.div`
    flex: 1;
    overflow: auto;
    width: 100%;
    position: relative;
`;

const SitesTableContainer = styled.div<{ $visibleDatesCount: number }>`
    display: grid;
    grid-template-columns: minmax(200px, 250px) repeat(${(props) => props.$visibleDatesCount}, minmax(70px, 1fr));
    min-width: max(600px, ${(props) => 150 + props.$visibleDatesCount * 70}px);
    width: 100%;
    border-left: 1px solid ${({ theme }) => theme.lightBorderColor};
    border-right: 1px solid ${({ theme }) => theme.lightBorderColor};
    border-bottom: 1px solid ${({ theme }) => theme.lightBorderColor};
    border-radius: 0 0 8px 8px;
    position: relative;
`;

const HeaderRow = styled.div`
    display: contents;
`;

const WeekHeaderRow = styled.div`
    display: contents;
`;

const WeekSiteHeaderCell = styled.div`
    background-color: ${({ theme }) => theme.cardBackgroundColor};
    border-top: 1px solid ${({ theme }) => theme.lightBorderColor};
    border-right: 1px solid ${({ theme }) => theme.lightBorderColor};
    position: sticky;
    top: 0;
    left: 0;
    z-index: 20;
    height: 20px;
`;

const WeekHeaderCell = styled.div<{
    $isWeekStart: boolean;
    $hasWeekDelimiter: boolean;
    $weekSpan: number;
    $isFirstWeekDelimiter: boolean;
}>`
    background-color: ${({ theme }) => theme.cardBackgroundColor};
    padding: 2px 4px;
    text-align: center;
    border-top: 1px solid ${({ theme }) => theme.lightBorderColor};
    border-right: 1px solid ${({ theme }) => theme.lightBorderColor};
    position: sticky;
    left: 0;
    top: 0;
    z-index: 10;
    height: 20px;
    display: flex;
    align-items: center;
    justify-content: center;

    ${({ $hasWeekDelimiter, $weekSpan, $isFirstWeekDelimiter, theme }) =>
        $hasWeekDelimiter &&
        `
        grid-column: span ${$weekSpan};
        border-left: ${$isFirstWeekDelimiter ? 'none' : `3px solid ${theme.lightBorderColor}`};
        border-right: 1px solid ${theme.lightBorderColor};
        border-bottom: 1px solid ${theme.lightBorderColor};
        position: sticky;
        top: 0;
        z-index: 15;
        background-color: ${theme.cardBackgroundColor};
        margin: 0;
        padding: 0;
        width: 100%;
        box-sizing: border-box;
    `}

    ${({ $isWeekStart, $hasWeekDelimiter, theme }) =>
        $isWeekStart &&
        !$hasWeekDelimiter &&
        `
        border-left: 3px solid ${theme.lightBorderColor};
    `}

    &:last-child {
        border-right: none;
    }
`;

const SiteHeaderCell = styled.div`
    background-color: ${({ theme }) => theme.cardBackgroundColor};
    color: ${({ theme }) => theme.textColor};
    padding: 8px;
    font-weight: 600;
    font-size: 16px;
    border-bottom: 1px solid ${({ theme }) => theme.lightBorderColor};
    border-right: 1px solid ${({ theme }) => theme.lightBorderColor};
    position: sticky;
    left: 0;
    z-index: 20;
    top: 20px;
    display: flex;
    align-items: center;
    gap: 8px;
`;

const DateHeaderCell = styled.div<{ $isWeekStart: boolean; $isFirstWeekStart: boolean }>`
    background-color: ${({ theme }) => theme.cardBackgroundColor};
    color: ${({ theme }) => theme.textColor};
    padding: 4px;
    text-align: center;
    font-weight: 600;
    font-size: 14px;
    border-bottom: 1px solid ${({ theme }) => theme.lightBorderColor};
    border-right: 1px solid ${({ theme }) => theme.lightBorderColor};
    position: sticky;
    left: 0;
    top: 20px;
    z-index: 10;

    ${({ $isWeekStart, $isFirstWeekStart, theme }) =>
        $isWeekStart &&
        !$isFirstWeekStart &&
        `
        border-left: 3px solid ${theme.lightBorderColor};
    `}

    &:last-child {
        border-right: none;
    }
`;

const WeekDelimiter = styled.div`
    font-size: 12px;
    font-weight: 700;
    text-transform: uppercase;
    padding: 2px 4px;
    background-color: #6461612a;
    color: ${({ theme }) => theme.textColor};
    width: 100%;
    height: 100%;
    text-align: center;
    display: flex;
    align-items: center;
    justify-content: center;
    margin: 0;
    box-sizing: border-box;

    @media (max-width: 1400px) {
        font-size: 8px;
    }
`;

const DateHeaderContent = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 1px;
    min-height: 40px;
    justify-content: space-between;
    padding: 2px 1px;
`;

const WeekdayLabel = styled.div`
    font-weight: 600;
    font-size: 16px;
    color: ${({ theme }) => theme.textColor};
    line-height: 1.1;
    white-space: nowrap;

    @media (max-width: 1400px) {
        font-size: 12px;
    }
`;

const DateLabel = styled.div`
    font-weight: 500;
    font-size: 16px;
    padding: 2px 4px;
    color: ${({ theme }) => theme.textColor};
    opacity: 0.8;
    line-height: 1.1;
    white-space: nowrap;

    @media (max-width: 1400px) {
        font-size: 12px;
    }
`;

const DeliveryCountLabel = styled.div`
    font-weight: 600;
    font-size: 14px;
    color: #2196f3;
    background-color: #2196f31a;
    padding: 2px 6px;
    border-radius: 10px;
    margin-bottom: 2px;
    text-align: center;
    white-space: nowrap;

    @media (max-width: 1400px) {
        font-size: 11px;
        padding: 1px 4px;
    }
`;

const SiteRow = styled.div`
    display: contents;

    &:last-child > * {
        border-bottom: none;
    }
`;

const SiteInfoCell = styled.div`
    padding: 8px;
    background-color: ${({ theme }) => theme.cardBackgroundColor};
    border-bottom: 1px solid ${({ theme }) => theme.lightBorderColor};
    border-right: 1px solid ${({ theme }) => theme.lightBorderColor};
    position: sticky;
    left: 0;
    z-index: 15;
    display: flex;
    align-items: center;
    gap: 8px;
`;

const SiteSelectionCheckbox = styled.input`
    width: 16px;
    height: 16px;
    margin: 0;
    cursor: pointer;
`;

const DateCheckboxCell = styled.div<{
    $isActive?: boolean;
    $exists?: boolean;
    $isClosed?: boolean;
    $isLaunchOrRemoval?: boolean;
    $isWeekStart?: boolean;
    $isFirstWeekStart?: boolean;
}>`
    padding: 8px;
    text-align: center;
    border-bottom: 1px solid ${({ theme }) => theme.lightBorderColor};
    border-right: 1px solid ${({ theme }) => theme.lightBorderColor};
    background-color: transparent;
    display: flex;
    align-items: center;
    justify-content: center;
    position: relative;

    ${({ $isWeekStart, $isFirstWeekStart, theme }) =>
        $isWeekStart &&
        !$isFirstWeekStart &&
        `
        border-left: 3px solid ${theme.lightBorderColor};
    `}

    ${({ $isClosed, theme }) =>
        $isClosed &&
        `
        &::before {
            content: '';
            position: absolute;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            background: repeating-linear-gradient(
                45deg,
                transparent,
                transparent 4px,
                ${theme.lightBorderColor} 4px,
                ${theme.lightBorderColor} 6px
            );
            pointer-events: none;
            z-index: 1;
        }
    `}

    &:last-child {
        border-right: none;
    }
`;

const HeaderControls = styled.div`
    display: flex;
    align-items: center;
`;

const HeaderCheckbox = styled.input`
    width: 20px;
    height: 20px;
    cursor: pointer;
    accent-color: #2196f3;

    @media (max-width: 1400px) {
        width: 18px;
        height: 18px;
    }
`;

const SortButton = styled.button<{ $isActive: boolean }>`
    background: none;
    border: none;
    cursor: pointer;
    font-size: 12px;
    color: ${({ $isActive, theme }) => ($isActive ? theme.ctaPrimaryColor || '#2196F3' : theme.textColor)};
    padding: 1px;
    position: absolute;
    right: 6px;
    &:hover {
        color: ${({ theme }) => theme.ctaPrimaryColor || '#2196F3'};
    }
`;

const CheckboxInput = styled.input<{
    $isActive: boolean;
    $exists: boolean;
    $isClosed: boolean;
    $isLaunchOrRemoval: boolean;
    $isDeliveryDay: boolean;
}>`
    width: 26px;
    height: 26px;
    cursor: ${({ $isClosed }) => ($isClosed ? 'not-allowed' : 'pointer')};
    position: relative;
    z-index: 2;

    @media (max-width: 1400px) {
        width: 22px;
        height: 22px;
    }

    /* Force specific colors and borders based on delivery type */
    ${({ $exists, $isClosed, $isLaunchOrRemoval, $isDeliveryDay }) => {
        if ($isClosed) {
            if ($exists && $isDeliveryDay) {
                return `
                    accent-color: #ccc !important;
                    opacity: 0.8;
                    box-shadow: 0 0 0 6px #bacfe79c;
                    background-color: #bacfe79c;
                `;
            }
            return `
                accent-color: #ccc !important;
                opacity: 0.8;
            `;
        }
        // launch or removal date (orange - impossible to uncheck)
        if ($isLaunchOrRemoval) {
            return `
                accent-color: #ff9800 !important;
                cursor: ${$exists ? 'not-allowed' : 'pointer'} !important;
            `;
        }
        // Delivery day with delivery planning (blue with box-shadow)
        if ($exists && $isDeliveryDay) {
            return `
                accent-color: #2196F3 !important;
                box-shadow: 0 0 0 6px #bacfe7;
                background-color: #bacfe7;
                `;
        }
        // Normal delivery (blue - checked) without box-shadow
        return `
            accent-color: #2196F3 !important;
        `;
    }}
`;
